import {
  storedSetCompletedAt,
  type LogSet,
  type LogSetKind,
} from '@heraion/shared';

import type { HistorySession } from './entryHistoryPick';

// LG-49 — ONE row shape for "a set the athlete did", whichever door it came
// through.
//
// The logger can learn about a past set two ways now: out of the rolling
// session window (`entryHistoryPick`, sets shaped as `WorkoutSet`, dated by
// their session) or straight off the per-exercise index
// (`useExerciseSetsFallback`, sets shaped as `LogSet`, each carrying its own
// `performedAt`). Same fact, two schemas — `WorkoutSet.type` and
// `LogSet.kind` are the same six-member union under different names.
//
// ⛔ The panel must not learn both. A component that branches on where a set
// came from grows a second rendering path, and the two drift — which is the
// whole reason this normalises here, in a pure module, instead of in the JSX.
//
// ⭐ Rows are SETS, not sessions. Antenore's rule for this slice: show the
// last five SETS with their date and the intensity that was recorded. The old
// panel showed up to three sessions and printed no effort at all, so "100 × 8"
// sat there without the one number that says what it cost.

export type HistorySetRow = {
  /** When the set happened, epoch ms. */
  atMs: number;
  kind: LogSetKind;
  reps: number | null;
  weightKg: number | null;
  durationSec: number | null;
  distanceM: number | null;
  /** Taken to failure — resolves to maximal effort downstream. */
  fail: boolean;
  rir: number | null;
  rpe: number | null;
};

/** How many rows the panel shows. Antenore's number for this slice. */
export const HISTORY_SET_ROWS = 5;

// Both source schemas carry these fields under the SAME names, optional and
// numeric, so one reader serves both rather than two six-armed switches kept
// in step by hand. Anything absent or non-finite reads as "not stated".
//
// ⛔ Typed as the real unions' shared shape, NOT as `Record<string, unknown>`.
// A cast to unknown-valued fields would accept any object at all: the day
// `WorkoutSet.weight` is renamed `weightKg`, `tsc` would stay silent and every
// history row would quietly print no load. A field that vanishes has to break
// the build, not the screen
// ([[feedback_a_test_double_cast_is_a_disabled_contract]]).
type SetFieldSource = Partial<
  Record<'reps' | 'weight' | 'duration' | 'distanceM' | 'rir' | 'rpe', number>
> & { fail?: boolean };

const num = (v: number | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const rowFrom = (
  set: SetFieldSource,
  kind: LogSetKind,
  atMs: number,
): HistorySetRow => ({
  atMs,
  kind,
  reps: num(set.reps),
  weightKg: num(set.weight),
  durationSec: num(set.duration),
  distanceM: num(set.distanceM),
  fail: set.fail === true,
  rir: num(set.rir),
  rpe: num(set.rpe),
});

const newestFirst = (a: HistorySetRow, b: HistorySetRow) => b.atMs - a.atMs;

const parsed = (iso: string | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

/** Rows out of the rolling session window. */
export function rowsFromSessions(
  sessions: readonly HistorySession[],
  limit: number = HISTORY_SET_ROWS,
): HistorySetRow[] {
  const rows: HistorySetRow[] = [];
  for (const s of sessions) {
    // A set's own `completedAt` when it has one, else the session's start.
    // Falling back matters for old rows: without it a whole session would
    // sort as epoch 0 and the "last five" would be the five oldest.
    const sessionAtMs = parsed(s.startedAt);
    if (sessionAtMs === null) continue;
    for (const set of s.entry.sets) {
      const atMs = parsed(set.completedAt);
      rows.push(rowFrom(set, set.type, atMs ?? sessionAtMs));
    }
  }
  return rows.sort(newestFirst).slice(0, limit);
}

/** Rows straight off the per-exercise index. */
export function rowsFromLogSets(
  sets: readonly LogSet[],
  limit: number = HISTORY_SET_ROWS,
): HistorySetRow[] {
  const rows: HistorySetRow[] = [];
  for (const s of sets) {
    const atMs = parsed(storedSetCompletedAt(s));
    if (atMs === null) continue;
    rows.push(rowFrom(s, s.kind, atMs));
  }
  return rows.sort(newestFirst).slice(0, limit);
}

/** When this exercise was last done — the fact the age pill states.
 *  Null when there is nothing to date. */
export const lastPerformedAtMs = (
  rows: readonly HistorySetRow[],
): number | null => (rows.length === 0 ? null : (rows[0]?.atMs ?? null));
